import type { Brand, PhoneModel } from '../types';

export type GlobalModelSearchEntry = Readonly<{
    brand: Brand;
    model: PhoneModel;
}>;

const normalize = (value: unknown): string => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/gu, '')
    .toLowerCase()
    .replace(/\+/gu, ' plus ')
    .replace(/[^a-z0-9]+/gu, ' ')
    .trim()
    .replace(/\s+/gu, ' ');

const hardwareCodeVariant = (token: string): string => {
    if (!/[a-z]/u.test(token) || !/\d/u.test(token)) return token;
    const withoutMarketSuffix = token.replace(/(\d)[a-z]{1,3}$/u, '$1');
    return withoutMarketSuffix.length >= 3 ? withoutMarketSuffix : token;
};

const tokenMatches = (queryToken: string, searchableTokens: Set<string>): boolean => {
    if (searchableTokens.has(queryToken)) return true;
    const queryHardwareCode = hardwareCodeVariant(queryToken);
    if (queryHardwareCode !== queryToken && searchableTokens.has(queryHardwareCode)) return true;
    return queryToken.length >= 2 && [...searchableTokens].some(token => token.startsWith(queryToken));
};

const searchableValues = ({ brand, model }: GlobalModelSearchEntry): string[] => {
    const brandName = normalize(brand.name);
    const values = [
        brandName,
        normalize(brand.slug),
        normalize(model.name),
        normalize(model.id),
        normalize(model.slug),
        normalize(model.masterStableId),
        normalize(`${brand.name} ${model.name}`),
    ];

    // Samsung markets the repair catalogue under the Galaxy family name even
    // when the stored Model label is intentionally concise (for example S24).
    if (brandName === 'samsung') values.push('galaxy');
    return values.filter(Boolean);
};

const scoreEntry = (entry: GlobalModelSearchEntry, query: string): number | null => {
    const queryTokens = normalize(query)
        .split(' ')
        .filter(token => token && token !== 'sm');
    if (!queryTokens.length) return null;

    const values = searchableValues(entry);
    const combined = normalize(values.join(' '));
    const tokens = new Set(combined.split(' ').filter(Boolean));
    for (const token of [...tokens]) tokens.add(hardwareCodeVariant(token));
    if (!queryTokens.every(token => tokenMatches(token, tokens))) return null;

    const brand = normalize(entry.brand.name);
    const model = normalize(entry.model.name);
    const brandAndModel = normalize(`${entry.brand.name} ${entry.model.name}`);
    const normalizedQuery = normalize(query);

    if (normalizedQuery === brandAndModel) return 1000;
    if (normalizedQuery === model) return 950;
    if (brandAndModel.startsWith(normalizedQuery)) return 900;
    if (model.startsWith(normalizedQuery)) return 850;
    if (normalizedQuery === brand) return 700;
    return 500 + queryTokens.length * 10;
};

export const searchGlobalModels = (
    entries: ReadonlyArray<GlobalModelSearchEntry>,
    query: string,
    limit = 8,
): GlobalModelSearchEntry[] => entries
    .map((entry, index) => ({ entry, index, score: scoreEntry(entry, query) }))
    .filter((candidate): candidate is { entry: GlobalModelSearchEntry; index: number; score: number } => candidate.score !== null)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, limit)
    .map(candidate => candidate.entry);
